
/// <amd-module name="Environments/EnvironmentRunDetails/Components/EnvironmentRunDetailsComponent" />
import React = require("react");

import { getTabs } from "VSS/Features/PlatformUI/ContributedTab";
import { Filter } from "VSSUI/Utilities/Filter";
import { Header, TitleSize } from "VSSUI/Header";
import { HeaderCommandBarWithFilter } from "VSSUI/HeaderCommandBar";
import { IReadonlyObservableArray, ObservableValue } from "VSS/Core/Observable";
import { IVssComponentProperties, VssComponent, IVssComponentContext } from "VSS/Platform/Layout";
import { onClickFPS } from "VSS/Platform/FPS";
import { IVssContributedTab, TabBar, TabContent } from "VSSUI/Tabs";
import { IVssHistoryService } from "VSS/Platform/History";
import { Page } from "VSSUI/Page";
import { localeFormat } from "VSS/Core/Util/String";
import { Surface, SurfaceBackground } from "VSSUI/Surface";
import { EnvironmentDeploymentExecutionRecord, EnvironmentExpands, EnvironmentInstance } from "DistributedTask/Client/Contracts/TaskAgent";

import { addBreadcrumbs } from "Environments/Breadcrumb/EnvironmentsHubBreadcrumbService";
import * as Resources from "Environments/EnvironmentRunDetails/Resources";
import { IEnvironmentDeploymentHistoryService } from "Environments/Client/EnvironmentDeploymentHistoryService";
import { IEnvironmentService } from "Environments/Client/EnvironmentService";
import {
    getCurrentEnvironmentIdFromRoute,
    getEnvironmentPageUrl,
    getEnvironmentsRunDetailsPageUrl,
    getHistoryStateAsString,
    getHistoryStateAsNumber
} from "Environments/EnvironmentsCommon/Link";
import { EnvironmentTabKeys } from "Environments/EnvironmentsCommon/Utils";

const EnvironmentRunDetailsDefaultTabKey = "ms.vss-environments-web.environment-rundetails-jobshistory-tab";

interface IEnvironmentRunDetailsState {
    definitionName: string;
    ownerName: string;
    ownerId: number;
    environmentName: string;
}

class HistoryStateKeys {
    public static View = "view";
    public static EnvironmentExecutionRecordId = "environmentExecutionRecordId";
    public static scopeId = "scopeId";
    public static ownerId = "ownerId";
    public static definitionId = "definitionId";
    public static planType = "planType";
}

class EnvironmentRunDetailsTabKeys {
    public static EnvironmentJobsHistoryTabKey = "jobshistory";
    public static EnvironmentCommitsTabKey = "commits";
    public static EnvironmentWorkItemsTabKey = "workitems";
}

class EnvironmentRunDetailsComponent extends VssComponent<IVssComponentProperties, IEnvironmentRunDetailsState> {

    public constructor(props: IVssComponentProperties, context: IVssComponentContext) {
        super(props, context);

        this._instanceId = "environment-run-details-page-" + this.componentId;

        this._initializeViewState(context);

        this.state = { definitionName: "", ownerName: "", ownerId: 0, environmentName: "" };

        this._tabProvider = getTabs(
            context.pageContext,
            "ms.vss-environments-web.environment-rundetails-tab-group",
            {
                props: {
                    instanceId: this._instanceId,
                    environmentId: this._environmentId,
                    environmentExecutionRecordId: this._environmentExecutionRecordId,
                    scopeId: this._scopeId,
                    ownerId: this._ownerId,
                    definitionId: this._definitionId,
                    planType: this._planType,
                    filter: this._filter,
                    filterToggled: this._filterToggled
                }
            }
        );

        this._getMetaInfo();

        this._onSelectedTabChanged(this._selectedTabId.value);
    }

    public render(): JSX.Element {
        return (
            <Surface background={SurfaceBackground.neutral}>
                <Page className="flex-grow" tabProviders={this._tabProvider} selectedTabId={this._selectedTabId}>
                    <Header title={localeFormat(Resources.RunsTitle, this.state.ownerName)} backButtonProps={{ onClick: this._onBackButtonClick }} description={localeFormat(Resources.RunsDescription, this.state.ownerId, this.state.definitionName, this.state.environmentName)} titleSize={TitleSize.Large} />
                    <TabBar onSelectedTabChanged={this._onSelectedTabChanged} renderAdditionalContent={this._renderTabBarCommands} />
                    <TabContent />
                </Page>
            </Surface>
        );
    }

    private _initializeViewState(context: IVssComponentContext): void {
        this._historyService = context.pageContext.getService<IVssHistoryService>("IVssHistoryService");
        this._environmentId = getCurrentEnvironmentIdFromRoute(context.pageContext);
        this._environmentExecutionRecordId = getHistoryStateAsNumber(context.pageContext, HistoryStateKeys.EnvironmentExecutionRecordId);
        this._scopeId = getHistoryStateAsString(context.pageContext, HistoryStateKeys.scopeId);
        this._planType = getHistoryStateAsString(context.pageContext, HistoryStateKeys.planType);
        this._definitionId = getHistoryStateAsNumber(context.pageContext, HistoryStateKeys.definitionId);
        this._ownerId = getHistoryStateAsNumber(context.pageContext, HistoryStateKeys.ownerId);
        this._selectedTabId.value = getHistoryStateAsString(context.pageContext, HistoryStateKeys.View);
    }

    private _onBackButtonClick = (): void => {
        onClickFPS(this.context.pageContext, getEnvironmentPageUrl(this.context.pageContext, this._environmentId, EnvironmentTabKeys.EnvironmentDeploymentsTabKey), true);
    }

    private _renderTabBarCommands = (): JSX.Element | undefined => {
        return this._selectedTabId.value !== EnvironmentRunDetailsTabKeys.EnvironmentJobsHistoryTabKey ? <HeaderCommandBarWithFilter filter={this._filter} filterToggled={this._filterToggled} items={[]} /> : undefined;
    };

    private _onSelectedTabChanged = (tabId: string): void => {
        if (!tabId) {
            tabId = EnvironmentRunDetailsTabKeys.EnvironmentJobsHistoryTabKey;
        }

        this._selectedTabId.value = tabId;
        this._updateHistoryState();
    }

    private _updateHistoryState(): void {
        const state = this._historyService.getState() || {};
        state[HistoryStateKeys.View] = this._selectedTabId.value;

        this._historyService.replaceState(state);
    }

    private _getMetaInfo(): void {
        const environmentHistoryService = this.context.pageContext.getService<IEnvironmentDeploymentHistoryService>("IEnvironmentDeploymentHistoryService");
        const environmentService = this.context.pageContext.getService<IEnvironmentService>("IEnvironmentService");

        let getEnvironmentDeploymentExecutionRecordsRestApiPromise = environmentHistoryService.getEnvironmentDeploymentExecutionRecordsRestApi(this._environmentId, this._environmentExecutionRecordId, 1);
        let getEnvironmentByIdPromise = environmentService.getEnvironmentById(this._environmentId, EnvironmentExpands.None);

        Promise.all([getEnvironmentDeploymentExecutionRecordsRestApiPromise, getEnvironmentByIdPromise]).then(([records, environment]) => {
            if (records && records.length > 0 && !!environment) {
                const deploymentRecord = records[0];
                this._addBreadcrumbs(environment, deploymentRecord);
                this.setState({
                    definitionName: deploymentRecord.definition.name,
                    ownerName: deploymentRecord.owner.name,
                    ownerId: deploymentRecord.owner.id,
                    environmentName: environment.name
                });
            }
        })
            .catch((error: any) => { console.log(error) });
    }

    private _addBreadcrumbs(environment: EnvironmentInstance, deploymentRecord: EnvironmentDeploymentExecutionRecord): void {
        const pageContext = this.context.pageContext;
        const environmentsPageUrl = getEnvironmentPageUrl(pageContext, environment.id);
        const runDetailsPageUrl = getEnvironmentsRunDetailsPageUrl(
            pageContext,
            environment.id,
            deploymentRecord.id,
            deploymentRecord.owner.id,
            deploymentRecord.definition.id,
            deploymentRecord.planType
        );

        addBreadcrumbs(
            pageContext,
            { name: environment.name, url: environmentsPageUrl },
            { name: localeFormat(Resources.RunsTitle, deploymentRecord.owner.name), url: runDetailsPageUrl }
        );
    }

    private readonly _instanceId: string;
    private readonly _selectedTabId = new ObservableValue<string>(EnvironmentRunDetailsDefaultTabKey);
    private _tabProvider: IReadonlyObservableArray<IVssContributedTab>;
    private _historyService: IVssHistoryService;
    private _environmentId: number;
    private _scopeId: string;
    private _ownerId: number;
    private _definitionId: number;
    private _planType: string;
    private _environmentExecutionRecordId: number;
    private _filter = new Filter();
    private _filterToggled = new ObservableValue<boolean>(false);
}

VssComponent.register("environmentrundetails", EnvironmentRunDetailsComponent);
